import { useState } from 'react';
import {
  Button,
  Box,
  Grid,
  Card,
  CardMedia,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { CloudUpload, Delete, PictureAsPdf } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { jsPDF } from 'jspdf';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export default function ImageToPDFConverter() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleChange = (event) => {
    const files = Array.from(event.target.files);
    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setImages((prev) => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    URL.revokeObjectURL(images[index].preview);
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Helper function to get image dimensions from a data URL
  const getImageDimensions = (dataUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = reject;
      img.src = dataUrl;
    });
  };

  const handleConvert = async () => {
    if (images.length === 0) return;
    setLoading(true);

    const pdf = new jsPDF();

    for (let i = 0; i < images.length; i++) {
      // Read file as data URL
      const dataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(images[i].file);
      });

      // Get actual image dimensions
      const { width: imgWidth, height: imgHeight } = await getImageDimensions(dataUrl);

      // Get PDF page size
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Calculate scaling to fit image within PDF page (preserve aspect ratio)
      let width = pdfWidth;
      let height = (imgHeight * pdfWidth) / imgWidth;

      if (height > pdfHeight) {
        height = pdfHeight;
        width = (imgWidth * pdfHeight) / imgHeight;
      }

      // Center the image on the page
      // const x = (pdfWidth - width) / 2;
      // const y = (pdfHeight - height) / 2;

      // Determine image format
      const format = images[i].file.type === 'image/png' ? 'PNG' : 'JPEG';

      if (i !== 0) pdf.addPage();
      pdf.addImage(dataUrl, format, 0, 0, width, height);
    }

    pdf.save('jmk-image-to-pdf.pdf');
    setLoading(false);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Button
        component="label"
        variant="contained"
        startIcon={<CloudUpload />}
        sx={{ mb: 2, mr: 2 }}
      >
        Select Images
        <VisuallyHiddenInput
          type="file"
          multiple
          accept="image/*"
          onChange={handleChange}
        />
      </Button>
      {/* <Button
        component="label"
        variant="contained"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2, ml: 2 }}
        onClick={() => navigate(-1)}>
        Back
      </Button> */}

      {images.length > 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 2, maxHeight: 400, overflow: 'auto' }}>
            {images.map((img, idx) => (
              <Grid item xs={6} sm={4} md={3} key={idx}>
                <Card sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    image={img.preview}
                    alt={`preview ${idx}`}
                    sx={{ height: 140, objectFit: 'cover' }}
                  />
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'white' }}
                    onClick={() => removeImage(idx)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Button
            variant="contained"
            color="secondary"
            startIcon={loading ? <CircularProgress size={20} /> : <PictureAsPdf />}
            onClick={handleConvert}
            disabled={loading}
          >
            {loading ? 'Creating PDF...' : `Convert ${images.length} image(s) to PDF`}
          </Button>
        </>
      )}
    </Box>
  );
}